import * as React from "react";
import { useErrorBoundaryStyles } from "./styles";
import { Button } from "@fluentui/react-components";
import { deleteAllFromStorage } from "@/taskpane/lib";
import { FallbackRender } from "@sentry/react";
import log from "../../utils/logger";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    log.error("Error caught by boundary", undefined, { error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // We need to use the styles hook inside a function component
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

export const ErrorFallback = ({ error }: FallbackRender["arguments"]) => {
  const styles = useErrorBoundaryStyles();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefreshClick = async () => {
    await deleteAllFromStorage();
    setIsRefreshing(true);
    window.location.reload();
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Something went wrong</h2>
      <Button className={styles.message} onClick={handleRefreshClick} disabled={isRefreshing}>
        {isRefreshing ? "Refreshing..." : "Try again"}
      </Button>
      {error && <pre className={styles.errorMessage}>{error.message}</pre>}
    </div>
  );
};
